import { Head, usePage } from '@inertiajs/react'
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { AppSidebar } from './AppSidebar'
import { DashboardPageProps } from '@/types'

type BreadcrumbItem = {
  label: string
  href?: string
}

type DashboardLayoutProps = {
  children: React.ReactNode
  title: string
  breadcrumbs?: BreadcrumbItem[]
}

export function DashboardLayout({ children, title, breadcrumbs = [] }: DashboardLayoutProps) {
  const { flash, auth } = usePage<DashboardPageProps>().props
  const accountSlug = auth.account?.slug

  const defaultBreadcrumbs: BreadcrumbItem[] = [
    { label: auth.account?.name || 'Workspace', href: `/${accountSlug}/dashboard` },
    { label: title },
  ]

  const finalBreadcrumbs = breadcrumbs.length > 0 ? breadcrumbs : defaultBreadcrumbs

  // Auto-prepend account base path to relative breadcrumb hrefs
  const resolvedBreadcrumbs = finalBreadcrumbs.map((item) => ({
    ...item,
    href: item.href && !item.href.startsWith('/') ? `/${accountSlug}/${item.href}` : item.href,
  }))

  return (
    <>
      <Head title={title} />
      <div className="font-display">
        <SidebarProvider>
          <AppSidebar />
          <SidebarInset>
            <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border/40 px-4">
              <SidebarTrigger className="-ml-1" />
            </header>

            {(flash.notice || flash.alert) && (
              <div className="border-b border-border/40 px-4 py-3">
                {flash.notice && (
                  <div className="rounded-lg bg-blue-50 border border-blue-200/60 p-3 text-sm text-blue-700">
                    {flash.notice}
                  </div>
                )}
                {flash.alert && (
                  <div className="rounded-lg bg-red-50 border border-red-200/60 p-3 text-sm text-red-700">
                    {flash.alert}
                  </div>
                )}
              </div>
            )}

            <main className="flex-1 p-6">
              <div className="max-w-2xl mx-auto">
                <Breadcrumb className="mb-4">
                  <BreadcrumbList>
                    {resolvedBreadcrumbs.map((item, index) => (
                      <span key={index} className="contents">
                        {index > 0 && <BreadcrumbSeparator />}
                        <BreadcrumbItem>
                          {item.href ? (
                            <BreadcrumbLink href={item.href}>{item.label}</BreadcrumbLink>
                          ) : (
                            <BreadcrumbPage>{item.label}</BreadcrumbPage>
                          )}
                        </BreadcrumbItem>
                      </span>
                    ))}
                  </BreadcrumbList>
                </Breadcrumb>
                {children}
              </div>
            </main>
          </SidebarInset>
        </SidebarProvider>
      </div>
    </>
  )
}
